import AlipayIcon from '@/assets/icons/trade/alipay.svg'
import WeixinPayIcon from '@/assets/icons/trade/weixin-pay.svg'
import Alert from '@/components/Alert/Alert'
import { HStack } from '@/components/ui/hstack'
import { IconSymbol } from '@/components/ui/IconSymbol'
import { Image } from '@/components/ui/image'
import { Pressable } from '@/components/ui/pressable'
import { Text } from '@/components/ui/text'
import { VStack } from '@/components/ui/vstack'
import useHorizontalSafeAreaPadding from '@/lib/hooks/useHorizontalSafeAreaPadding'
import usePlatformColor from '@/lib/hooks/usePlatformColor'
import { toRgba } from '@/lib/utils/colors'
import type { CurrentUser } from '@/redux/slices/currentUserSlice'
import { getGdMerchantState, switchTradeMerchantType } from '@/redux/slices/tradeSlice'
import { useAppDispatch } from '@/redux/store'
import { MerchantType } from '@/typings/trade'
import { useTheme } from 'expo-router/react-navigation'
import type { ReactNode } from 'react'
import { ScrollView, View } from 'react-native'

const DEFAULT_MERCHANT_AVATAR = 'https://gd-fe-assets.jinshujucdn.com/trade-miniapp/assets/merchant-default-avatar.png'

type Props = {
  billingAccount: NonNullable<CurrentUser['billingAccount']>
  merchantType: MerchantType
  availableMerchantTypes: MerchantType[]
  onSwitched: () => void
}

export default function SwitchMerchant({ billingAccount, merchantType, availableMerchantTypes, onSwitched }: Props) {
  const dispatch = useAppDispatch()
  const { paddingLeft, paddingRight } = useHorizontalSafeAreaPadding()
  const [backgroundColor] = usePlatformColor('systemBackground')
  // The brand blue the tab bar highlights with, not iOS systemBlue, so the screen matches the active tab
  const primaryColor = useTheme().colors.primary as string
  const { r, g, b } = toRgba(primaryColor)

  const gdMerchant = billingAccount.gdMerchant
  const gdMerchantState = getGdMerchantState(billingAccount)
  const gdMerchantAvatar = gdMerchant?.merchantAvatar?.visitUrl || DEFAULT_MERCHANT_AVATAR
  const gdMerchantName = gdMerchant?.merchantName || '小金商户'

  const directAccounts = [
    ...billingAccount.wxpayAccounts.map(account => ({
      key: account.id,
      name: account.mchName || '微信支付商户',
      icon: <WeixinPayIcon width={28} height={28} />,
    })),
    ...billingAccount.alipayAccounts.map(account => ({
      key: account.id,
      name: account.email,
      icon: <AlipayIcon width={28} height={28} />,
    })),
  ]

  const isGdMerchant = merchantType === MerchantType.GdMerchant
  const targetType = isGdMerchant ? MerchantType.Direct : MerchantType.GdMerchant
  const targetLabel = isGdMerchant ? '直连商户' : '小金商户'

  const handleSwitch = () => {
    Alert.alert(`确认切换至${targetLabel}？`, `切换后，交易与订单等内容将更新为${targetLabel}下的相关数据。`, [
      { text: '取消', style: 'cancel' },
      {
        text: '确定',
        isPreferred: true,
        onPress: async () => {
          await dispatch(switchTradeMerchantType(targetType))
          onSwitched()
        },
      },
    ])
  }

  const boundMerchants: { key: string; name: string; icon: ReactNode }[] = isGdMerchant
    ? directAccounts
    : gdMerchantState === 'none'
      ? []
      : [
          {
            key: 'gd_merchant',
            name: gdMerchantState === 'opened' ? gdMerchantName : `${gdMerchantName}（申请中）`,
            icon: (
              <Image source={{ uri: gdMerchantAvatar }} alt="" size="none" className="w-[24px] h-[24px] rounded-full" />
            ),
          },
        ]

  return (
    <ScrollView
      contentInsetAdjustmentBehavior="automatic"
      style={{ backgroundColor }}
      // Spacing is in points, measured from the design; Tailwind spacing here is rem-scaled.
      contentContainerStyle={{
        paddingLeft: paddingLeft + 16,
        paddingRight: paddingRight + 16,
        paddingTop: 8,
        paddingBottom: 32,
      }}
    >
      <View
        className="bg-secondary-system-background"
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          borderRadius: 18,
          paddingHorizontal: 16,
          paddingVertical: 14,
          gap: 12,
        }}
      >
        {isGdMerchant ? (
          // gluestack Image sizes from className and ignores style width/height
          <Image source={{ uri: gdMerchantAvatar }} alt="" size="none" className="w-[50px] h-[50px] rounded-full" />
        ) : (
          <View
            className="items-center justify-center bg-system-background"
            style={{ width: 50, height: 50, borderRadius: 25 }}
          >
            <IconSymbol name="building.2" size={24} color="#8e8e93" />
          </View>
        )}
        <VStack className="flex-1" style={{ gap: 2 }}>
          <Text className="leading-[26px] text-label font-semibold" style={{ fontSize: 19 }} numberOfLines={1}>
            {isGdMerchant ? gdMerchantName : '直连商户'}
          </Text>
          <Text className="leading-[20px] text-secondary-label" style={{ fontSize: 14 }} numberOfLines={1}>
            {isGdMerchant
              ? `商户号：${gdMerchant?.merchantNo ?? '-'}`
              : `商户简称：${directAccounts.map(account => account.name).join('，')}`}
          </Text>
        </VStack>
        <View
          style={{
            backgroundColor: `rgba(${r}, ${g}, ${b}, 0.12)`,
            borderRadius: 6,
            paddingHorizontal: 8,
            paddingVertical: 3,
          }}
        >
          <Text className="leading-[20px]" style={{ color: primaryColor, fontSize: 14 }}>
            当前账户
          </Text>
        </View>
      </View>

      {availableMerchantTypes.includes(targetType) && (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={`切换至${targetLabel}`}
          onPress={handleSwitch}
          className="flex-row items-center justify-center data-[active=true]:opacity-80"
          style={{ backgroundColor: primaryColor, height: 44, borderRadius: 22, gap: 10, marginTop: 27 }}
        >
          <IconSymbol name="arrow.left.arrow.right" size={16} color="#ffffff" />
          <Text className="leading-[22px] text-white font-medium" style={{ fontSize: 16 }}>
            切换至{targetLabel}
          </Text>
        </Pressable>
      )}

      {boundMerchants.length > 0 && (
        <View
          className="bg-secondary-system-background"
          style={{
            flexDirection: 'column',
            borderRadius: 18,
            paddingHorizontal: 16,
            paddingTop: 14,
            paddingBottom: 15,
            gap: 9,
            marginTop: 16,
          }}
        >
          <Text className="leading-[20px] text-secondary-label" style={{ fontSize: 14 }}>
            已绑定以下商户
          </Text>
          {boundMerchants.map(merchant => (
            <HStack key={merchant.key} className="items-center" style={{ gap: 10 }}>
              {merchant.icon}
              <Text className="leading-[24px] text-label flex-1" style={{ fontSize: 16 }} numberOfLines={1}>
                {merchant.name}
              </Text>
            </HStack>
          ))}
        </View>
      )}
    </ScrollView>
  )
}
